import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LEGAL_LINKS, PLATFORM_PUBLIC_ORIGIN } from '../content/legalPolicies';
import './cookieBanner.css';

const accept = { externalContent: true, diagnostics: true };
const reject = { externalContent: false, diagnostics: false };

const PolicyLinks = () => <span className="cookie-policy-links">{LEGAL_LINKS.map(link => <a key={link.path} href={`${PLATFORM_PUBLIC_ORIGIN}${link.path}`} target="_blank" rel="noopener noreferrer">{link.label}</a>)}</span>;

export default function CookieBanner({ visible, preferences, preferencesOpen, onOpen, onClose, onSave }) {
  const [draft, setDraft] = useState(preferences);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!preferencesOpen) return undefined;
    setDraft(preferences);
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button')?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
      else (document.querySelector('[data-cookie-customize]') || document.querySelector('.footer-cookie-preferences'))?.focus();
    };
  }, [preferencesOpen, preferences]);

  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled)');
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  return <>
    {visible && !preferencesOpen && (
      <section className="cookie-banner" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-description">
        <button type="button" className="cookie-close" aria-label="Chiudi e rifiuta i facoltativi" onClick={() => onSave(reject)}>×</button>
        <div className="cookie-banner-copy">
          <h2 id="cookie-banner-title">Le tue scelte sulla privacy</h2>
          <p id="cookie-banner-description">I servizi necessari consentono accesso, navigazione e sicurezza. Con il tuo consenso possiamo caricare video e immagini esterni e attivare la diagnostica facoltativa. Puoi cambiare idea in ogni momento da “Preferenze cookie”.</p>
          <PolicyLinks />
        </div>
        <div className="cookie-actions">
          <button type="button" className="cookie-button" onClick={() => onSave(reject)}>Rifiuta facoltativi</button>
          <button type="button" className="cookie-button cookie-button-secondary" data-cookie-customize onClick={onOpen}>Personalizza</button>
          <button type="button" className="cookie-button" onClick={() => onSave(accept)}>Accetta tutto</button>
        </div>
      </section>
    )}
    {preferencesOpen && createPortal(
      <div className="cookie-modal-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
        <section className="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookie-preferences-title" ref={dialogRef} onKeyDown={onKeyDown}>
          <button type="button" className="cookie-close" aria-label="Chiudi preferenze cookie" onClick={onClose}>×</button>
          <h2 id="cookie-preferences-title">Preferenze cookie</h2>
          <p>Seleziona soltanto i servizi che desideri abilitare. Le scelte facoltative sono disattivate per impostazione predefinita.</p>
          <div className="cookie-category"><strong>Servizi necessari</strong><span className="cookie-required">Sempre attivi</span><p>Autenticazione, navigazione, salvataggio della scelta e protezione antiabuso reCAPTCHA nei moduli in cui è configurata.</p></div>
          <div className="cookie-category"><label><input type="checkbox" checked={draft.externalContent} onChange={event => setDraft({ ...draft, externalContent: event.target.checked })} />Contenuti esterni</label><p>Consente di caricare video e immagini da fornitori esterni, come YouTube. Senza questa scelta, i contenuti restano sostituiti da un avviso.</p></div>
          <div className="cookie-category"><label><input type="checkbox" checked={draft.diagnostics} onChange={event => setDraft({ ...draft, diagnostics: event.target.checked })} />Diagnostica facoltativa</label><p>Consente l’invio di errori del browser a Sentry, se il servizio è configurato. Non è necessaria per partecipare agli eventi.</p></div>
          <PolicyLinks />
          <div className="cookie-actions">
            <button type="button" className="cookie-button" onClick={() => onSave(reject)}>Rifiuta facoltativi</button>
            <button type="button" className="cookie-button" onClick={() => onSave(accept)}>Accetta tutto</button>
            <button type="button" className="cookie-button cookie-button-secondary" onClick={() => onSave(draft)}>Salva preferenze</button>
          </div>
        </section>
      </div>, document.body,
    )}
  </>;
}
